/**
 * Parse a user-supplied invite string into one of three shapes:
 *  - a token invite (new, `?token=…` format or a bare token)
 *  - a legacy hh_id invite (`?hh_id=…` format)
 *  - invalid (empty / no recognizable token)
 *
 * Mirrors iOS `resolveInvite(...)` parsing in `OnboardingGateView`.
 */
export type ParsedInvite =
  | { kind: "token"; token: string }
  | { kind: "hh_id"; householdId: string }
  | { kind: "invalid" };

export function parseInviteInput(raw: string): ParsedInvite {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "invalid" };

  // Try to parse as a URL first. Allow query strings without a scheme by
  // falling back to a prefix that URL() will accept.
  let url: URL | null = null;
  try {
    url = new URL(trimmed);
  } catch {
    // Might be something like `zoeyorganizer.com/join?token=…` with no scheme.
    try {
      url = new URL(`https://${trimmed}`);
    } catch {
      url = null;
    }
  }

  if (url) {
    const token = url.searchParams.get("token");
    if (token && token.trim()) return { kind: "token", token: token.trim() };
    const hhId = url.searchParams.get("hh_id");
    if (hhId && hhId.trim()) return { kind: "hh_id", householdId: hhId.trim() };
  }

  // No URL structure — treat as a raw token.
  return { kind: "token", token: trimmed };
}
